/**
 * Client-side validation rules. Mirrors the extended `ValidateDefinition`
 * rule catalog from the primitives implementation plan so that when the
 * backend is wired up the UI and server agree about what "valid" means.
 */

import { getPrimitive, PRIMITIVE_BY_KEY } from '~/pages/workflows/builder/fixtures/registry';
import { INTEGRATION_BY_KEY } from '~/pages/workflows/builder/fixtures/capabilities';
import { effectiveOutputs, effectiveInputs, isAssignable, isInputLike } from './selectors';
import type { DefinitionState, ValidationIssue } from '~/pages/workflows/builder/types';

export function validate(state: DefinitionState): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const nodeById = new Map(state.nodes.map((n) => [n.id, n]));
  const slotById = new Map(state.inputs.map((s) => [s.key, s]));

  // Rule: every input slot has a matching input node
  const ownedSlots = new Set(state.nodes.map((n) => n.ownedSlotKey).filter(Boolean) as string[]);
  for (const slot of state.inputs) {
    if (!ownedSlots.has(slot.key)) {
      issues.push({
        level: 'warning',
        code: 'orphan-slot',
        message: `Input "${slot.label}" has no Input step feeding it. Delete the slot or add an Input step.`
      });
    }
  }

  for (const node of state.nodes) {
    const primitive = PRIMITIVE_BY_KEY[node.taskKey];
    if (!primitive) {
      issues.push({
        nodeId: node.id,
        level: 'error',
        code: 'unknown-task',
        message: `Unknown task "${node.taskKey}". This is probably a corrupted draft.`
      });
      continue;
    }
    const effInputs = effectiveInputs(node);

    // Polymorphic Input: must have a subtype picked
    if (
      primitive.category === 'input' &&
      !node.inputSubtype &&
      !node.taskKey.startsWith('INPUT_')
    ) {
      issues.push({
        nodeId: node.id,
        level: 'error',
        code: 'input-not-configured',
        message:
          'Pick an input type (Document, Spreadsheet, Text, JSON, AMS record, or Web lookup).'
      });
    }

    for (const field of effInputs) {
      const shown = fieldVisible(field, node.config);
      if (!shown) continue;

      if (isInputLike(field.fieldKind)) {
        const binding = node.bindings.find((b) => b.inputKey === field.key);
        if (field.required && !binding) {
          issues.push({
            nodeId: node.id,
            fieldKey: field.key,
            level: 'error',
            code: 'missing-binding',
            message: `"${field.label}" is required — pick a source.`
          });
        }
        if (binding && binding.source.type === 'TASK_OUTPUT') {
          const src = binding.source;
          const source = nodeById.get(src.sourceNodeId);
          if (!source) {
            issues.push({
              nodeId: node.id,
              fieldKey: field.key,
              level: 'error',
              code: 'dangling-binding',
              message: `"${field.label}" is bound to a deleted step.`
            });
          } else {
            const out = effectiveOutputs(source).find((o) => o.key === src.sourceOutputKey);
            if (!out) {
              issues.push({
                nodeId: node.id,
                fieldKey: field.key,
                level: 'error',
                code: 'dangling-output',
                message: `"${field.label}" is bound to an output that no longer exists.`
              });
            } else {
              if (!isAssignable(out.valueType, field.valueType)) {
                issues.push({
                  nodeId: node.id,
                  fieldKey: field.key,
                  level: 'warning',
                  code: 'type-mismatch',
                  message: `"${field.label}" expects ${field.valueType}; source provides ${out.valueType}.`
                });
              }
              if (out.isList && !field.isList && !node.forEach) {
                issues.push({
                  nodeId: node.id,
                  fieldKey: field.key,
                  level: 'warning',
                  code: 'list-scalar-mismatch',
                  message: `"${field.label}" expects a single value, but source is a list. Aggregate first or enable per-item.`
                });
              }
            }
          }
        }
        if (binding && binding.source.type === 'WORKFLOW_INPUT') {
          const slot = slotById.get(binding.source.workflowInputKey);
          if (!slot) {
            issues.push({
              nodeId: node.id,
              fieldKey: field.key,
              level: 'error',
              code: 'dangling-slot',
              message: `"${field.label}" is bound to a deleted input slot.`
            });
          }
        }
      } else if (field.fieldKind === 'CONFIG') {
        const value = node.config[field.key];
        if (field.required && (value === undefined || value === '' || value === null)) {
          issues.push({
            nodeId: node.id,
            fieldKey: field.key,
            level: 'error',
            code: 'missing-config',
            message: `"${field.label}" is required.`
          });
        }
      }
    }

    // forEach over non-enumerable
    const fe = node.forEach;
    if (fe) {
      if (fe.kind === 'WORKFLOW_INPUT') {
        const slot = slotById.get(fe.sourceWorkflowInputKey);
        if (slot && !slot.enumerable) {
          issues.push({
            nodeId: node.id,
            level: 'error',
            code: 'foreach-non-enumerable',
            message: `Per-item is enabled, but "${slot.label}" isn't a list.`
          });
        }
      } else {
        const source = nodeById.get(fe.sourceNodeId);
        if (source) {
          const out = effectiveOutputs(source).find((o) => o.key === fe.sourceOutputKey);
          if (out && !out.isList) {
            issues.push({
              nodeId: node.id,
              level: 'error',
              code: 'foreach-non-enumerable',
              message: `Per-item is enabled, but "${out.label}" is not a list.`
            });
          }
        }
      }
    }

    // Primitive-specific checks
    if (node.taskKey === 'EXTRACT') {
      const entities = (node.config.entities as { outputKey: string }[] | undefined) ?? [];
      if (entities.length === 0) {
        issues.push({
          nodeId: node.id,
          level: 'warning',
          code: 'extract-no-entities',
          message: 'Select at least one thing to extract.'
        });
      }
    }
    if (node.taskKey === 'AGGREGATE') {
      const strategy = node.config.strategy as string | undefined;
      if (strategy === 'llm' && !(node.config.prompt as string | undefined)?.trim()) {
        issues.push({
          nodeId: node.id,
          fieldKey: 'prompt',
          level: 'error',
          code: 'llm-no-prompt',
          message: 'LLM strategy needs a prompt.'
        });
      }
    }
    if (node.taskKey === 'OUTPUT_INTEGRATION') {
      const integrationType = node.config.integrationType as string | undefined;
      if (integrationType && !INTEGRATION_BY_KEY[integrationType]) {
        issues.push({
          nodeId: node.id,
          level: 'error',
          code: 'unknown-integration',
          message: `Unknown integration "${integrationType}".`
        });
      }
      const actions = (node.config.actions as { capabilityKey: string }[] | undefined) ?? [];
      if (actions.length === 0) {
        issues.push({
          nodeId: node.id,
          level: 'warning',
          code: 'integration-no-actions',
          message: 'Add at least one action.'
        });
      }
      if (integrationType) {
        const type = INTEGRATION_BY_KEY[integrationType];
        for (const action of actions) {
          if (type && !type.capabilities.find((c) => c.key === action.capabilityKey)) {
            issues.push({
              nodeId: node.id,
              level: 'error',
              code: 'unknown-capability',
              message: `Unknown action "${action.capabilityKey}".`
            });
          }
        }
      }
    }
  }

  // Dangling nodes (no output reaches any Output node)
  const outputNodes = state.nodes.filter((n) => getPrimitive(n.taskKey).category === 'output');
  const reachable = new Set<string>();
  function walk(id: string) {
    if (reachable.has(id)) return;
    reachable.add(id);
    const node = nodeById.get(id);
    if (!node) return;
    for (const b of node.bindings) {
      if (b.source.type === 'TASK_OUTPUT') walk(b.source.sourceNodeId);
    }
    if (node.forEach?.kind === 'TASK_OUTPUT') walk(node.forEach.sourceNodeId);
  }
  outputNodes.forEach((n) => walk(n.id));
  for (const node of state.nodes) {
    if (reachable.has(node.id)) continue;
    if (getPrimitive(node.taskKey).category === 'output') continue;
    if (state.nodes.length <= 1) continue;
    issues.push({
      nodeId: node.id,
      level: 'warning',
      code: 'dangling-node',
      message: 'This step is not connected to any output.'
    });
  }

  return issues;
}

function fieldVisible(
  field: { when?: { field: string; equals: string[] } },
  config: Record<string, unknown>
): boolean {
  if (!field.when) return true;
  const value = config[field.when.field];
  return field.when.equals.includes(String(value ?? ''));
}

export function issuesForNode(issues: ValidationIssue[], nodeId: string): ValidationIssue[] {
  return issues.filter((i) => i.nodeId === nodeId);
}

export function issuesForField(
  issues: ValidationIssue[],
  nodeId: string,
  fieldKey: string
): ValidationIssue[] {
  return issues.filter((i) => i.nodeId === nodeId && i.fieldKey === fieldKey);
}
